/**
 * r2.ts — Section 1: "R² — How Much Variance Did We Explain?".
 *
 * A scatter of N points around the line y = 0.8x + 2, with the OLS best-fit
 * line drawn through them. The "Noise" slider sweeps the cloud from a perfect
 * line (R² = 1) to heavy scatter (R² → 0), and the decomposition updates live:
 * SS_tot (variance of y), SS_res (leftover after the fit), and R² itself. The
 * R² formula re-typesets via MathJax on every input, like derivative.ts.
 *
 * R² is *not* "accuracy" — it is the fraction of y's variance the line
 * accounts for. A horizontal cloud (no relationship) gets R² near 0; the line
 * has nothing to explain.
 */

import { render } from './math';
import { regressionPoints, ols, type Point } from './data';

// Canvas x ∈ [0, 10], y ∈ [-2, 14] — fits the cloud plus headroom.
const X_MIN = 0;
const X_MAX = 10;
const Y_MIN = -2;
const Y_MAX = 14;

export function initR2(): void {
  const canvasEl = document.getElementById('r2Canvas') as HTMLCanvasElement | null;
  const sliderEl = document.getElementById('r2NoiseSlider') as HTMLInputElement | null;
  const noiseValEl = document.getElementById('r2NoiseValue');
  const ssTotEl = document.getElementById('r2SsTot');
  const ssResEl = document.getElementById('r2SsRes');
  const r2ValueEl = document.getElementById('r2Value');
  const formulaEl = document.getElementById('r2Formula');
  const statusEl = document.getElementById('r2Status');
  if (
    !canvasEl || !sliderEl || !noiseValEl || !ssTotEl || !ssResEl ||
    !r2ValueEl || !formulaEl || !statusEl
  ) return;

  // Local non-null aliases so the type narrowing carries into draw()/update().
  // Without these, TS widens `sliderEl` back to `... | null` inside the nested
  // closures (the guard above only narrows the linear scope).
  const canvas = canvasEl;
  const slider = sliderEl;
  const noiseVal = noiseValEl;

  const ctx = canvas.getContext('2d');
  if (!ctx) return;
  const W = canvas.width;
  const H = canvas.height;

  // 40 px of padding so points near the axes don't kiss the canvas edge.
  const PAD = 40;
  const mapX = (x: number): number => PAD + ((x - X_MIN) / (X_MAX - X_MIN)) * (W - 2 * PAD);
  const mapY = (y: number): number => H - PAD - ((y - Y_MIN) / (Y_MAX - Y_MIN)) * (H - 2 * PAD);

  function drawAxes(): void {
    ctx!.strokeStyle = '#e2e8f0'; // slate-200
    ctx!.lineWidth = 1;
    ctx!.beginPath();
    // Horizontal gridlines every 4 units of y.
    for (let y = Y_MIN; y <= Y_MAX; y += 4) {
      ctx!.moveTo(mapX(X_MIN), mapY(y));
      ctx!.lineTo(mapX(X_MAX), mapY(y));
    }
    // Vertical gridlines every 2 units of x.
    for (let x = X_MIN; x <= X_MAX; x += 2) {
      ctx!.moveTo(mapX(x), mapY(Y_MIN));
      ctx!.lineTo(mapX(x), mapY(Y_MAX));
    }
    ctx!.stroke();
  }

  function draw(points: Point[], slope: number, intercept: number): void {
    const meanY = points.reduce((s, p) => s + p.y, 0) / points.length;
    let ssTot = 0;
    let ssRes = 0;
    for (const p of points) {
      ssTot += (p.y - meanY) ** 2;
      const pred = slope * p.x + intercept;
      ssRes += (p.y - pred) ** 2;
    }
    const r2 = ssTot === 0 ? 1 : 1 - ssRes / ssTot;

    ctx!.clearRect(0, 0, W, H);
    drawAxes();

    // Mean-of-y reference line — the baseline model R² is measured against.
    // Drawing it first, faintly, makes the "variance around this line" idea
    // visible: SS_tot is the spread of points around the flat mean line.
    ctx!.strokeStyle = '#cbd5e1'; // slate-300
    ctx!.lineWidth = 1;
    ctx!.setLineDash([4, 4]);
    ctx!.beginPath();
    ctx!.moveTo(mapX(X_MIN), mapY(meanY));
    ctx!.lineTo(mapX(X_MAX), mapY(meanY));
    ctx!.stroke();
    ctx!.setLineDash([]);

    // Best-fit line, extended across the canvas.
    ctx!.strokeStyle = '#4c72b0'; // blue, shared with the other essay figures
    ctx!.lineWidth = 3;
    ctx!.beginPath();
    ctx!.moveTo(mapX(X_MIN), mapY(slope * X_MIN + intercept));
    ctx!.lineTo(mapX(X_MAX), mapY(slope * X_MAX + intercept));
    ctx!.stroke();

    // Residual segments — thin vertical lines from each point to the fit.
    // Faint slate; they're context, not the focus. Drawn before the dots so
    // the dots sit on top.
    ctx!.strokeStyle = '#94a3b8'; // slate-400
    ctx!.lineWidth = 1;
    for (const p of points) {
      const pred = slope * p.x + intercept;
      ctx!.beginPath();
      ctx!.moveTo(mapX(p.x), mapY(p.y));
      ctx!.lineTo(mapX(p.x), mapY(pred));
      ctx!.stroke();
    }

    // Scatter points on top.
    ctx!.fillStyle = '#4c72b0';
    for (const p of points) {
      ctx!.beginPath();
      ctx!.arc(mapX(p.x), mapY(p.y), 4, 0, Math.PI * 2);
      ctx!.fill();
    }

    // Numeric readouts + live formula.
    noiseVal!.textContent = (parseFloat(slider.value)).toFixed(2);
    ssTotEl!.textContent = ssTot.toFixed(2);
    ssResEl!.textContent = ssRes.toFixed(2);
    r2ValueEl!.textContent = r2.toFixed(3);

    const tex = `$$ R^2 = 1 - \\frac{SS_{res}}{SS_{tot}} = 1 - \\frac{${ssRes.toFixed(2)}}{${ssTot.toFixed(2)}} = ${r2.toFixed(3)} $$`;
    void render(formulaEl!, tex);

    // Status line: short, opinionated — R² is "fraction of variance explained".
    if (r2 > 0.9) {
      statusEl!.textContent = `The line explains ${(r2 * 100).toFixed(0)}% of the variance in y — a tight fit.`;
      statusEl!.className = 'text-sm mt-3 font-semibold text-emerald-600';
    } else if (r2 > 0.5) {
      statusEl!.textContent = `${(r2 * 100).toFixed(0)}% of variance explained — useful but noisy.`;
      statusEl!.className = 'text-sm mt-3 font-semibold text-amber-600';
    } else {
      statusEl!.textContent = `Only ${(r2 * 100).toFixed(0)}% explained — the line barely beats the flat mean.`;
      statusEl!.className = 'text-sm mt-3 font-semibold text-rose-600';
    }
  }

  function update(): void {
    const noiseScale = parseFloat(slider.value);
    const points = regressionPoints(noiseScale);
    const { slope, intercept } = ols(points);
    draw(points, slope, intercept);
  }

  slider.addEventListener('input', update);
  update();
}
